/** @jsxImportSource theme-ui */
import React, { useState, useEffect } from "react";
import { Box } from "theme-ui";
import PropTypes from "prop-types";
import { colors, typography } from "../../styles/theme";

const Tab = ({ label, children, onClick }) => <div>{children}</div>;

const styles = {
  tabList: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",
    rowGap: "0.5rem",
    cursor: "pointer",
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  tabItem: {
    padding: "0.5rem 1.5rem",
    borderRadius: "72px",
    ...typography.textMd500,
    textDecoration: "none",
    transition: "color 0.3s",
  },
};

const Tabs = ({ children, customStyles, selectedTab }) => {
  const [activeTab, setActiveTab] = useState();

  useEffect(() => {
    if (selectedTab !== undefined) {
      const selectedTabIndex = React.Children.toArray(children).findIndex(
        (child) => child.props.label === selectedTab
      );
      if (selectedTabIndex !== -1) {
        setActiveTab(selectedTabIndex);
      }
    }
  }, [selectedTab, children]);

  const handleTabClick = (index, onClick) => {
    setActiveTab(index);
    if (onClick) onClick();
  };

  return (
    <Box sx={customStyles}>
      <ul sx={styles.tabList}>
        {React.Children.map(children, (child, index) => {
          const isActive = index === activeTab;
          return (
            <li
              key={child.props.label}
              onClick={() => handleTabClick(index, child.props.onClick)}
              sx={{
                ...styles.tabItem,
                background: isActive ? colors.primaryMain : "transparent",
                color: isActive ? colors.white : colors.neutral800,
                ":hover": {
                  color: isActive ? colors.white : colors.primaryMain,
                },
              }}
            >
              {child.props.label}
            </li>
          );
        })}
      </ul>
      <Box
        sx={{
          paddingTop: "1rem",
        }}
      >
        {children[activeTab]}
      </Box>
    </Box>
  );
};

Tab.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
};

Tabs.propTypes = {
  children: PropTypes.arrayOf(PropTypes.element).isRequired,
  customStyles: PropTypes.object,
  selectedTab: PropTypes.string,
};

export { Tabs, Tab };
